import { useState, useEffect, useRef, useMemo } from 'react';
import './input-autocomplete.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const InputAutocomplete = ({ options, onChange, onBlur, value, placeholder, title, className, error }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const optionsContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (value) {
      if (typeof value === 'object' && value.label) {
        setSearchTerm(value.label);
      } else {
        const matchingOption = options.find((option) => option.value === value);
        if (matchingOption) {
          setSearchTerm(matchingOption.label);
        } else if (typeof value === 'string') {
          setSearchTerm(value);
        }
      }
    } else {
      setSearchTerm('');
    }
  }, [value, options]);

  const memoizedOptions = useMemo(() => options, [options]);

  useEffect(() => {
    setFilteredOptions(memoizedOptions);
  }, [memoizedOptions]);

  const selectOption = (event, newValue) => {
    event.stopPropagation();
    onChange(newValue);
    setShowOptions(false);
    setSearchTerm(newValue.label);
    setFilteredOptions(options);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (newSearchTerm) => {
    setShowOptions(true);
    setSearchTerm(newSearchTerm);
    if (newSearchTerm === '') {
      setFilteredOptions(options);
      setHighlightedIndex(-1);
      onChange({ label: '', value: '' });
      setShowOptions(false);
    }
    else if (newSearchTerm) {
      const lowerCaseValue = newSearchTerm.toLowerCase();
      const newFilteredOptions = options.filter((option) => option.label?.toLowerCase().includes(lowerCaseValue));
      setFilteredOptions(newFilteredOptions);
      setHighlightedIndex(newFilteredOptions.length > 0 ? 0 : -1);
    } else {
      setFilteredOptions(options);
      setHighlightedIndex(options.length > 0 ? 0 : -1);
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowOptions(false);
      setFilteredOptions(options);
      setHighlightedIndex(-1);
    }
  };

  useEffect(() => {
    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  const handleKeyDown = (e) => {
    if (!showOptions || filteredOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        selectOption(e, filteredOptions[highlightedIndex]);
      }
    }
  };

  useEffect(() => {
    if (optionsContainerRef.current && highlightedIndex >= 0) {
      const optionElements = optionsContainerRef.current.querySelectorAll('.option');
      if (optionElements[highlightedIndex]) {
        optionElements[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, showOptions]);

  const handleFocus = () => {
    setShowOptions(true);
  };

  const randomId = useMemo(() => {
    return Math.random().toString(36).substring(2, 8);
  }, []);

  const inputId = title 
    ? `autocomplete-${title.replace(/\s+/g, '-').toLowerCase()}` 
    : `autocomplete-${randomId}`;

  return (
    <div className={className}>
      <div className='form-group position-relative' ref={wrapperRef}>
        {title && <label htmlFor={inputId} className='mb-1'>{title}</label>}
        <div className='input-group'>
          <input
            ref={inputRef}
            id={inputId}
            className={`form-control rounded ${error ? 'is-invalid' : ''}`}
            value={searchTerm}
            placeholder={placeholder}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleFocus}
            onBlur={(e) => {
              setTimeout(() => {
                if (onBlur) {
                  onBlur(e.target.value || { label: '', value: '' });
                }
                setShowOptions(false);
              }, 200);
            }}
            onKeyDown={handleKeyDown}
            autoComplete='off'
            type='text'
            aria-expanded={showOptions}
            aria-autocomplete="list"
            aria-controls={showOptions ? "autocomplete-listbox" : undefined}
            aria-activedescendant={highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined}
          />
          <span 
            className='input-group-text ml-1 pointer'
            onClick={(e) => {
              e.stopPropagation();
              setShowOptions(!showOptions);
              if (!showOptions) inputRef.current?.focus();
            }}
            aria-label={showOptions ? "Hide options" : "Show options"}
          >
            <FontAwesomeIcon icon={faChevronDown} className={showOptions ? 'flipped' : 'regular'} />
          </span>
        </div>
        {error && <div className="invalid-feedback d-block">{error}</div>}
        {showOptions && (
          <div 
            className='p-2 border rounded mt-1 options-container bg-white' 
            ref={optionsContainerRef}
            id="autocomplete-listbox"
            role="listbox"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  id={`option-${index}`}
                  className={`p-1 pointer option${index === highlightedIndex ? ' bg-light' : ''}`}
                  onClick={(e) => selectOption(e, option)}
                  role='option'
                  aria-selected={index === highlightedIndex}
                  tabIndex='-1'
                >
                  {option.label}
                </div>
              ))
            ) : (
              <div className="p-1 text-muted">No matching options</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InputAutocomplete;
